import { apiFetch, apiPost } from './api'

export interface UserSummary {
  id: string
  givenName: string | null
  familyName: string | null
  email: string | null
}

// Module-level cache keyed by user id. Users rarely change, so a simple
// process-lifetime cache is sufficient and avoids repeated lookups when
// rendering many comments.
const userCache = new Map<string, UserSummary>()
// Track ids known to be missing so we don't keep re-requesting them.
const missingIds = new Set<string>()

/**
 * Batch-lookup user summaries from the server. Results are cached in-memory.
 * Returns a Map keyed by id so callers can join against comment.userId.
 */
export async function lookupUsers(ids: string[]): Promise<Map<string, UserSummary>> {
  const unique = Array.from(new Set(ids.filter((id): id is string => Boolean(id))))
  const toFetch = unique.filter((id) => !userCache.has(id) && !missingIds.has(id))

  if (toFetch.length > 0) {
    try {
      const fetched = await apiPost<UserSummary[], { ids: string[] }>('users/lookup', { ids: toFetch })
      const fetchedIds = new Set(fetched.map((u) => u.id))
      for (const user of fetched) {
        userCache.set(user.id, user)
      }
      // Any id we asked about but didn't get back doesn't exist in Postgres.
      for (const id of toFetch) {
        if (!fetchedIds.has(id)) missingIds.add(id)
      }
    } catch (error) {
      console.error('Failed to lookup users:', error)
    }
  }

  const result = new Map<string, UserSummary>()
  for (const id of unique) {
    const cached = userCache.get(id)
    if (cached) result.set(id, cached)
  }
  return result
}

// One in-flight/settled promise per contract so every comment editor on the page shares a
// single fetch. Access grants change rarely; a failed fetch is evicted so the next editor retries.
const mentionableCache = new Map<string, Promise<UserSummary[]>>()

/**
 * Users who may be @-mentioned in comments on this contract — the server returns exactly the
 * users who can access the document (and re-validates the same rule when the comment is saved).
 */
export function getMentionableUsers(contractUuid: string): Promise<UserSummary[]> {
  let pending = mentionableCache.get(contractUuid)
  if (!pending) {
    pending = apiFetch<UserSummary[]>(`contracts/${contractUuid}/mentionable-users`).catch((error) => {
      mentionableCache.delete(contractUuid)
      console.error('Failed to load mentionable users:', error)
      return []
    })
    mentionableCache.set(contractUuid, pending)
  }
  return pending
}

/**
 * Build a human-readable display name from a {@link UserSummary}.
 */
export function formatUserName(user: UserSummary | undefined, fallbackId?: string): string {
  if (!user) return fallbackId ? `User ${fallbackId.substring(0, 8)}` : 'Unknown user'
  const given = (user.givenName || '').trim()
  const family = (user.familyName || '').trim()
  if (given && family) return `${given} ${family}`
  if (given) return given
  if (family) return family
  if (user.email) return user.email
  return fallbackId ? `User ${fallbackId.substring(0, 8)}` : 'Unknown user'
}
